'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { CaretDown } from 'phosphor-icons';
import { useEffect, useMemo, useRef } from 'react';
import { OwnershipFilterProps } from './OwnershipFilter.types';
import { useOwnershipFilter } from './hooks/useOwnershipFilter';

export const OwnershipFilter = ({
  parentSelector,
  className = '',
  onToggle,
  isLoading = true, // default to true because coveo only updates the state after the first render
  searchEngine,
}: OwnershipFilterProps) => {
  const { checked, open, containerRef, handleChange, toggleOpen, close } =
    useOwnershipFilter({
      parentSelector,
      onToggle,
      searchEngine,
    });
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyFragment[];
  }>();

  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && checkboxRef.current) {
      checkboxRef.current.focus();
    }
  }, [open]);

  const label = useMemo(() => {
    const hideOwnedAssetsLabel = searchValuesMicrocopy?.find(
      (m) => m.key === 'search-ui-values.hide-owned-assets'
    )?.value;
    return hideOwnedAssetsLabel || 'Hide owned assets';
  }, [searchValuesMicrocopy]);

  const ownershipButtonLabel = useMemo(() => {
    const ownershipLabel = searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.ownership'
    )?.value;
    return ownershipLabel || 'Ownership';
  }, [searchLabelsMicrocopy]);

  if (isLoading) {
    return null;
  }

  return (
    <div className={className} ref={containerRef}>
      <div className="relative">
        <button
          id="ownership-button"
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls="ownership-menu"
          onClick={toggleOpen}
          data-test="ownership-filter-toggle"
          className="flex h-10 items-center gap-2 rounded-md border border-grey-400 bg-grey-100 px-3 text-grey-600 hover:text-grey-700 focus:bg-white focus:text-grey-700"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              toggleOpen();
            }
            if (e.key === 'Escape') {
              close();
            }
          }}
        >
          <span className="tiny-text font-medium">{ownershipButtonLabel}</span>
          <span
            className={
              open ? 'rotate-180 transition-transform' : 'transition-transform'
            }
          >
            <CaretDown
              size={15}
              className="pointer-events-none text-grey-600"
            />
          </span>
        </button>
        {open && (
          <div
            id="ownership-menu"
            role="group"
            aria-label="Ownership options"
            data-test="ownership-filter-menu"
            className="absolute z-10 mt-2 min-w-[208px] rounded-md border border-grey-200 bg-white p-1 shadow-md"
            tabIndex={-1}
          >
            <label className="relative flex cursor-pointer items-center rounded-sm px-2 py-1 text-grey-700 hover:bg-grey-200">
              <input
                type="checkbox"
                className="mr-2 h-4 w-4 cursor-pointer"
                checked={checked}
                onChange={handleChange}
                aria-checked={checked}
                ref={checkboxRef}
                data-test="ownership-filter-checkbox"
              />
              <span>{label}</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};
